/**
 * Carousel
 *
 * Accessible carousel following the WAI-ARIA Carousel pattern with Tabbed Navigation.
 *
 * Animation approach: all slides are absolutely stacked. CSS handles the
 * transitions via .is-active (crossfade bg, slide-in content). JS just
 * swaps classes and sets a brief direction hint (is-entering-next/prev)
 * so the content slides in from the correct side.
 *
 * - Arrow keys navigate tabs (roving tabindex) or cycle slides
 * - Home / End jump to first / last tab
 * - Live region announces slide changes to screen readers
 * - Respects prefers-reduced-motion
 */

const prefersReducedMotion = () =>
  window.matchMedia('(prefers-reduced-motion: reduce)').matches;

class Carousel {
  constructor(el) {
    this.root = el;
    this.slides = [...el.querySelectorAll('.carousel-slide')];
    this.tabs = [...el.querySelectorAll('[data-carousel-tab]')];
    this.liveRegion = el.querySelector('.carousel-live-region');
    this.current = 0;
    this.total = this.slides.length;
    this.transitioning = false;

    if (this.total < 2) return;

    this.bindEvents();
  }

  bindEvents() {
    // Prev / Next buttons (shown when sidebar nav is off)
    const prevBtn = this.root.querySelector('[data-carousel-prev]');
    const nextBtn = this.root.querySelector('[data-carousel-next]');
    prevBtn?.addEventListener('click', () =>
      this.goTo((this.current - 1 + this.total) % this.total)
    );
    nextBtn?.addEventListener('click', () =>
      this.goTo((this.current + 1) % this.total)
    );

    // Tab clicks
    this.tabs.forEach((tab) => {
      tab.addEventListener('click', () => {
        const index = parseInt(tab.dataset.carouselTab, 10);
        this.goTo(index);
      });

      // Roving tabindex keyboard navigation within tablist
      tab.addEventListener('keydown', (e) => this.handleTabKeydown(e));
    });

    // Keyboard on the whole carousel region (for when focus is inside a slide)
    this.root.addEventListener('keydown', (e) => this.handleRootKeydown(e));
  }

  handleTabKeydown(e) {
    const { key } = e;
    let handled = false;

    if (key === 'ArrowRight' || key === 'ArrowDown') {
      this.focusTab((this.current + 1) % this.total);
      handled = true;
    } else if (key === 'ArrowLeft' || key === 'ArrowUp') {
      this.focusTab((this.current - 1 + this.total) % this.total);
      handled = true;
    } else if (key === 'Home') {
      this.focusTab(0);
      handled = true;
    } else if (key === 'End') {
      this.focusTab(this.total - 1);
      handled = true;
    }

    if (handled) {
      e.preventDefault();
      e.stopPropagation();
    }
  }

  handleRootKeydown(e) {
    // Only handle if focus is not inside the tablist (tabs handle their own keys)
    if (e.target.closest('[role="tablist"]')) return;

    if (e.key === 'ArrowLeft') {
      this.goTo((this.current - 1 + this.total) % this.total);
      e.preventDefault();
    } else if (e.key === 'ArrowRight') {
      this.goTo((this.current + 1) % this.total);
      e.preventDefault();
    }
  }

  focusTab(index) {
    this.goTo(index);
    this.tabs[index]?.focus();
  }

  goTo(index) {
    if (index === this.current || this.transitioning) return;

    const oldSlide = this.slides[this.current];
    const newSlide = this.slides[index];
    const direction = index > this.current ? 'next' : 'prev';

    // Deactivate old slide
    oldSlide.classList.remove('is-active');
    oldSlide.setAttribute('aria-hidden', 'true');
    oldSlide.setAttribute('inert', '');

    if (!prefersReducedMotion()) {
      // Set direction hint so CSS starts the content off-screen
      newSlide.classList.add(`is-entering-${direction}`);

      // Force reflow so the browser registers the starting position
      void newSlide.offsetHeight;

      // Remove the hint — CSS transitions to the resting position
      newSlide.classList.remove(`is-entering-${direction}`);
    }

    // Activate new slide
    newSlide.classList.add('is-active');
    newSlide.removeAttribute('aria-hidden');
    newSlide.removeAttribute('inert');

    // Brief lock to prevent rapid-fire clicks during transition
    this.transitioning = true;
    setTimeout(() => {
      this.transitioning = false;
    }, prefersReducedMotion() ? 50 : 550);

    this.current = index;
    this.updateTabs();
    this.announce(index);
  }

  updateTabs() {
    this.tabs.forEach((tab, i) => {
      const isActive = i === this.current;
      tab.classList.toggle('is-active', isActive);
      tab.setAttribute('aria-selected', String(isActive));
      tab.setAttribute('tabindex', isActive ? '0' : '-1');
    });
  }

  announce(index) {
    if (!this.liveRegion) return;
    const slide = this.slides[index];
    const headline =
      slide.querySelector('.carousel-slide__headline')?.textContent ?? '';
    this.liveRegion.textContent = `Slide ${index + 1} of ${this.total}${headline ? ': ' + headline : ''}`;
  }
}

const initCarousels = () => {
  const carousels = document.querySelectorAll('[data-carousel]');
  carousels.forEach((el) => new Carousel(el));
};

document.addEventListener('DOMContentLoaded', initCarousels);
